/**
 * useAuth — TanStack Query hooks for authentication.
 *
 * Token invariants (CLAUDE.md / §13):
 *   - Access token: in-memory via lib/auth/tokens.ts
 *   - Refresh token: HTTP-only cookie managed by backend
 *
 * Three hooks exported:
 *   useLoginMutation()      — POST /auth/login + GET /auth/me on success
 *   useCurrentUserQuery()   — GET /auth/me, enabled when token is present
 *   useLogoutMutation()     — POST /auth/logout + full state teardown
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import type { AxiosError } from 'axios';

import { login, logout, getCurrentUser, changePassword, resetPasswordEmail } from '@/lib/api/auth';
import type {
  AccessTokenResponse,
  ChangePasswordRequest,
  LoginRequest,
  MessageResponse,
  ResetPasswordEmailResponse,
  UserMeResponse,
} from '@/lib/api/auth';
import { getAccessToken, setAccessToken } from '@/lib/auth/tokens';
import { resetClientSession } from '@/lib/auth/session';
import { useAuthStore } from '@/stores/auth-store';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

// ─── Query key ─────────────────────────────────────────────────────────────

export const currentUserKey = ['currentUser'] as const;

// ─── useBootAuth ───────────────────────────────────────────────────────────

/**
 * App-boot silent refresh.
 *
 * Access tokens are intentionally in-memory only (§13), so a page reload
 * loses them.  This hook runs ONCE on mount, calls /auth/refresh against the
 * HTTP-only cookie, and restores the access token if the cookie is still
 * valid.  The subsequent useCurrentUserQuery then sees the token and hydrates
 * the user.
 *
 * Returns `bootChecked` — flips to true after the refresh attempt resolves
 * (success or failure).  Route guards should wait for this before deciding
 * whether to redirect to /login.
 */
export function useBootAuth(): { bootChecked: boolean } {
  const [bootChecked, setBootChecked] = useState<boolean>(false);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post<{ access_token: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        if (!cancelled && data?.access_token) {
          setAccessToken(data.access_token);
        }
      } catch {
        // No valid refresh cookie — leave the token unset; route guards will
        // route to /login.
      } finally {
        if (!cancelled) {
          setBootChecked(true);
          // If no token was set, exit the loading state explicitly so
          // protected routes can redirect to /login instead of spinning.
          if (getAccessToken() === null) {
            setLoading(false);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setLoading]);

  return { bootChecked };
}

// ─── useCurrentUserQuery ───────────────────────────────────────────────────

/**
 * Fetches /auth/me when an access token is present in memory.
 * Refetches on window focus; staleTime 30 s (per project default).
 * On success, hydrates the Zustand auth-store with the user object.
 */
export function useCurrentUserQuery() {
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);

  return useQuery<UserMeResponse, AxiosError>({
    queryKey: currentUserKey,
    queryFn: async () => {
      const user = await getCurrentUser();
      setUser(user);
      setLoading(false);
      return user;
    },
    enabled: getAccessToken() !== null,
    staleTime: 30_000,
    retry: false,
  });
}

// ─── useLoginMutation ──────────────────────────────────────────────────────

/**
 * Pessimistic: POSTs credentials, on 200 stores the access token,
 * then fetches /me to hydrate the auth-store and query cache.
 * The caller handles navigation on success.
 */
export function useLoginMutation() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);

  return useMutation<AccessTokenResponse, AxiosError, LoginRequest>({
    mutationFn: login,
    onSuccess: async (data) => {
      // Clean slate FIRST — drop any state the previous user left behind (in
      // case their session ended without a clean logout) so this user never
      // inherits another account's cached data. Runs before we set the new
      // token so the fresh identity below is not wiped.
      resetClientSession();

      setAccessToken(data.access_token);

      // Fetch user identity and hydrate both Zustand store and query cache
      const user = await getCurrentUser();
      setUser(user);
      setLoading(false);
      queryClient.setQueryData<UserMeResponse>(currentUserKey, user);
    },
  });
}

// ─── useChangePasswordMutation ─────────────────────────────────────────────

/**
 * POST /api/v1/auth/change-password.
 *
 * Pessimistic — the backend rotates the credential, clears
 * must_change_password, and revokes all refresh tokens.  This hook deliberately
 * does NOT tear down local auth: the ChangePasswordPage re-authenticates with
 * the new password immediately after (minting a fresh, durable session) and
 * then navigates the user to their home dashboard.  onError is handled by the
 * caller (inline form error).
 */
export function useChangePasswordMutation() {
  return useMutation<MessageResponse, AxiosError, ChangePasswordRequest>({
    mutationFn: changePassword,
    // NO local auth teardown here.  The ChangePasswordPage immediately
    // re-authenticates with the new password (the backend revoked the old
    // refresh token), which replaces the in-memory access token and rehydrates
    // the auth store.  Clearing here would null the store mid-flow and make
    // RequireAuth bounce to /login before the re-login lands.  onError is
    // handled by the caller (inline form error).
  });
}

// ─── useResetPasswordEmailMutation ─────────────────────────────────────────

/**
 * POST /api/v1/users/{user_id}/reset-password-email.
 *
 * Pessimistic — admin action; backend generates a temp password, emails it,
 * and marks must_change_password=true.  Returns the temp password once so the
 * admin can display it in a dialog (e.g. if email delivery fails).
 */
export function useResetPasswordEmailMutation() {
  const queryClient = useQueryClient();

  return useMutation<ResetPasswordEmailResponse, AxiosError, string>({
    mutationFn: resetPasswordEmail,
    onSuccess: (_data, userId) => {
      // Invalidate the user detail so must_change_password reflects the new state.
      queryClient.invalidateQueries({ queryKey: ['users', 'detail', userId] });
    },
    // onError left to caller; they will show a toast via humanizeError.
  });
}

// ─── useLogoutMutation ─────────────────────────────────────────────────────

/**
 * POSTs /auth/logout, then ALWAYS tears down the full client session regardless
 * of the server response (token, auth store, per-user stores, query cache,
 * sessionStorage — see resetClientSession). Navigates to /login. The backend
 * revokes the refresh token and deletes its HttpOnly cookie, so nothing of the
 * previous user survives for whoever signs in next on this browser.
 */
export function useLogoutMutation() {
  const navigate = useNavigate();

  return useMutation<void, AxiosError, void>({
    mutationFn: async () => {
      try {
        await logout();
      } catch {
        // Always clear local state even if the server call fails
      }
    },
    onSettled: () => {
      resetClientSession();
      navigate('/login', { replace: true });
    },
  });
}
